/**
 * The client platform a request came from — the shared contract behind the admin panel's
 * "which apps has this member signed in from?" column.
 *
 * Nothing else in the stack knows whether a caller is the web SPA or a native build: the flow
 * transport is the same `POST /api/flows/*` from both, and Firebase Auth records the provider
 * (google.com, apple.com, password) but not the shell it was used in. So each client stamps
 * itself on every authenticated flow request via {@link CLIENT_PLATFORM_HEADER}, and the server
 * records the set on `users/{id}.derived.platforms`.
 *
 * The header is a *hint*, not a security boundary — a caller can send any value. It drives an
 * admin-panel display column only; never gate access or behaviour on it.
 */

import { z } from 'zod';

/** Request header carrying the caller's {@link ClientPlatform}. */
export const CLIENT_PLATFORM_HEADER = 'x-elara-client-platform';

/**
 * Platforms we distinguish. `web` is the Vite SPA (and Expo-web builds); `ios` / `android` are
 * native builds of the Expo app, mirroring React Native's `Platform.OS`.
 */
export const CLIENT_PLATFORMS = ['web', 'ios', 'android'] as const;

export const ClientPlatformSchema = z.enum(CLIENT_PLATFORMS);
export type ClientPlatform = z.infer<typeof ClientPlatformSchema>;

/** Human label for a platform badge in the admin panel. */
export const CLIENT_PLATFORM_LABELS: Record<ClientPlatform, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
};

/**
 * Narrows an untrusted header value to a known platform, returning `undefined` for anything
 * unrecognised (an old client, a curl request, a typo) so callers can simply skip recording.
 */
export function parseClientPlatform(raw: string | undefined | null): ClientPlatform | undefined {
  if (!raw) return;
  const parsed = ClientPlatformSchema.safeParse(raw.trim().toLowerCase());
  return parsed.success ? parsed.data : undefined;
}
